import React, { Dispatch, FC, SetStateAction } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { ImagePreviewCard } from '@components';
import { IMAGE_FORMATS } from '@constants/common';
import { generateFileUrl } from '@utils/common';
import { translate } from '@utils/locale';
import FileUploader from '@containers/file-uploader/FileUploader';

import { CreateProductFormFieldValues } from '../../types';

export type MultipleScreenshotUploadPropType = {
  productIndex: number;
  isEditMode: boolean;
  deletedProductAssetIds: string[];
  setDeletedProductAssetIds: Dispatch<SetStateAction<string[]>>;
};

const MultipleScreenshotUpload: FC<MultipleScreenshotUploadPropType> = ({
  productIndex,
  isEditMode,
  deletedProductAssetIds,
  setDeletedProductAssetIds
}) => {
  const {
    clearErrors,
    control,
    setValue,
    getValues,
    formState: { errors }
  } = useFormContext<CreateProductFormFieldValues>();

  const { fields: appScreenshotsFields } = useFieldArray({
    control,
    name: `products.${productIndex}.appScreenshots`
  });

  const { fields: webScreenshotsFields } = useFieldArray({
    control,
    name: `products.${productIndex}.webScreenshots`
  });

  const showAppProductScreenshots = (
    index: number,
    screenshotindex: number
  ) => {
    return !getValues(
      `products.${index}.appScreenshots.${screenshotindex}.path`
    );
  };

  const showWebProductScreenshots = (
    index: number,
    screenshotindex: number
  ) => {
    return !getValues(
      `products.${index}.webScreenshots.${screenshotindex}.path`
    );
  };

  const onAppFileUploadSuccess = (
    file: File,
    path: string,
    name: string,
    order: number
  ) => {
    setValue(name as `products.${number}.appScreenshots.${number}`, {
      id: '',
      file: file,
      path: path,
      fileName: file.name,
      order: order
    });
    clearErrors(name as never as keyof CreateProductFormFieldValues);
  };

  const onWebFileUploadSuccess = (
    file: File,
    path: string,
    name: string,
    order: number
  ) => {
    setValue(name as `products.${number}.webScreenshots.${number}`, {
      id: '',
      file: file,
      path: path,
      fileName: file.name,
      order: order
    });
    clearErrors(name as never as keyof CreateProductFormFieldValues);
  };

  const getAppImageUrl = (name: string) => {
    const fieldValue = getValues(
      name as `products.${number}.appScreenshots.${number}`
    );
    return generateFileUrl(fieldValue.path, fieldValue.file);
  };
  const getWebImageUrl = (name: string) => {
    const fieldValue = getValues(
      name as `products.${number}.webScreenshots.${number}`
    );
    return generateFileUrl(fieldValue.path, fieldValue.file);
  };

  const getAppFileName = (value: string) =>
    getValues(value as `products.${number}.appScreenshots.${number}`)?.fileName;

  const getWebFileName = (value: string) =>
    getValues(value as `products.${number}.appScreenshots.${number}`)?.fileName;

  const deleteAppPreview = (screenshotIndex: number) => {
    const productAsset = getValues(
      `products.${productIndex}.appScreenshots.${screenshotIndex}`
    );
    setDeletedProductAssetIds([...deletedProductAssetIds, productAsset.id]);

    setValue(`products.${productIndex}.appScreenshots.${screenshotIndex}`, {
      id: '',
      file: undefined,
      path: '',
      fileName: '',
      order: 0
    });
  };

  const deleteWebPreview = (screenshotIndex: number) => {
    const productAsset = getValues(
      `products.${productIndex}.webScreenshots.${screenshotIndex}`
    );
    setDeletedProductAssetIds([...deletedProductAssetIds, productAsset.id]);

    setValue(`products.${productIndex}.webScreenshots.${screenshotIndex}`, {
      id: '',
      file: undefined,
      path: '',
      fileName: '',
      order: 0
    });
  };

  return (
    <>
      <div className="flex flex-row items-center mt-5 -mb-5 w-full text-base font-normal">
        {translate('company.product.appScreenshots')}
      </div>
      {appScreenshotsFields.map((screenshot, screenshotIndex) => (
        <div key={screenshot.id}>
          <div className="flex flex-row items-center pb-2 mt-5 text-base font-normal text-black/60">
            {translate('company.product.appScreenshotCount', {
              count: screenshotIndex + 1
            })}
          </div>
          {showAppProductScreenshots(productIndex, screenshotIndex) ? (
            <FileUploader
              name={`products.${productIndex}.appScreenshots`}
              enableCrop={true}
              sizeInMb={3}
              supportedFormats={IMAGE_FORMATS}
              fileUploadSuccessHandler={(file, path) =>
                onAppFileUploadSuccess(
                  file,
                  path,
                  `products.${productIndex}.appScreenshots.${screenshotIndex}`,
                  screenshotIndex + 1
                )
              }
              deleteFileHandler={() => {
                deleteAppPreview(screenshotIndex);
              }}
              previousFile={
                getValues(
                  `products.${productIndex}.appScreenshots.${screenshotIndex}`
                )?.file
              }
              errors={{
                [`products.${productIndex}.appScreenshots.${screenshotIndex}`]:
                  errors.products?.[productIndex]?.appScreenshots?.[
                    screenshotIndex
                  ]
              }}
              customUploadText={translate(
                'company.placeholders.appScreenshots'
              )}
              aspectRatio={0.5}
              showPreview={true}
              isDisabled={!isEditMode}
            />
          ) : (
            <ImagePreviewCard
              name={`products.${productIndex}.appScreenshots.${screenshotIndex}`}
              isEdit={isEditMode}
              handleDeleteClick={() => deleteAppPreview(screenshotIndex)}
              imageUrl={getAppImageUrl(
                `products.${productIndex}.appScreenshots.${screenshotIndex}`
              )}
              fileName={getAppFileName(
                `products.${productIndex}.appScreenshots.${screenshotIndex}`
              )}
            />
          )}
        </div>
      ))}

      <div className="flex flex-row items-center mt-5 -mb-5 w-full text-base font-normal  ">
        {translate('company.product.webScreenshots')}
      </div>
      {webScreenshotsFields.map((screenshot, screenshotIndex) => (
        <div key={screenshot.id}>
          <div className="flex flex-row items-center pb-2 mt-5 text-base font-normal text-black/60 ">
            {translate('company.product.webScreenshotCount', {
              count: screenshotIndex + 1
            })}
          </div>
          {showWebProductScreenshots(productIndex, screenshotIndex) ? (
            <FileUploader
              name={`products.${productIndex}.webScreenshots`}
              enableCrop={true}
              sizeInMb={3}
              supportedFormats={IMAGE_FORMATS}
              fileUploadSuccessHandler={(file, path) =>
                onWebFileUploadSuccess(
                  file,
                  path,
                  `products.${productIndex}.webScreenshots.${screenshotIndex}`,
                  screenshotIndex + 1
                )
              }
              deleteFileHandler={() => {
                deleteWebPreview(screenshotIndex);
              }}
              previousFile={
                getValues(
                  `products.${productIndex}.webScreenshots.${screenshotIndex}`
                )?.file
              }
              errors={{
                [`products.${productIndex}.webScreenshots.${screenshotIndex}`]:
                  errors.products?.[productIndex]?.appScreenshots?.[
                    screenshotIndex
                  ]
              }}
              customUploadText={translate(
                'company.placeholders.webScreenshots'
              )}
              aspectRatio={4 / 3}
              showPreview={true}
              isDisabled={!isEditMode}
            />
          ) : (
            <ImagePreviewCard
              name={`products.${productIndex}.webScreenshots.${screenshotIndex}`}
              isEdit={isEditMode}
              handleDeleteClick={() => deleteWebPreview(screenshotIndex)}
              imageUrl={getWebImageUrl(
                `products.${productIndex}.webScreenshots.${screenshotIndex}`
              )}
              fileName={getWebFileName(
                `products.${productIndex}.webScreenshots.${screenshotIndex}`
              )}
            />
          )}
        </div>
      ))}
    </>
  );
};

export default MultipleScreenshotUpload;
